import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import classNames from "classnames";
import { sessionService } from "redux-react-session";

import { walletBackGroundImage } from "./images";
import Text from "./text/Text";
import {
  callAPIInterface,
  displayAmountWithCurrency,
  displayTaprootCurrency,
  getAmountFormatted,
  getCurrencyAmount,
  getDisplayAmount,
  getExchangeRate,
  showAmount,
  stableCoins,
} from "./constants";
import CommonSkeleton from "./CommonSkeleton";
import {
  btc,
  currentBalance,
  invalidExchangeRate,
  sats,
  swap,
  usdt,
} from "./messages";
import {
  setTaprootCurrency,
  setTransactionFilterData,
  showToast,
} from "../redux/actions";
import SelectionTabs from "./SelectionTabs";
import NewToSpeedWallet from "./NewToSpeedWallet";
import WalletSymbols from "./WalletSymbols";
import Button from "./button/Button";
import SwapCommon from "./swap/SwapCommon";
import SwapCurrencyDrawer from "./swap/SwapCurrencyDrawer";

const walletBGImage = (
  <img
    src={walletBackGroundImage}
    height="100%"
    width="100%"
    style={{
      position: "absolute",
      top: "0",
      right: "0",
      mixBlendMode: "multiply",
      filter: "brightness(0.97)",
      opacity: "0.4",
      objectFit: "cover",
    }}
  />
);

function WalletAmountBox({
  isFromBrowser,
  setButtonDisabled,
  setIsSupportedCurrency,
  setSelectedAsset,
  isShowSwapButton,
  handleSettings,
  currentSession,
  session,
  supportedSwaps,
}) {
  const dispatch = useDispatch();
  const imgSize = isFromBrowser ? 42 : 30;
  const satsAmountFontSize = isFromBrowser ? 44 : 34;
  const { taprootAsset, walletCurrency, accountAssets, showBtcAmount } =
    useSelector((state) => state.common);
  const { uuid: currency, icon: currencyIcon } = { ...taprootAsset };
  const { code: targetedCurrency, symbol: targetedCurrencySymbol } = {
    ...walletCurrency,
  };
  const currencyColor = classNames("bg-color-" + currency);

  const [targetAmount, setTargetAmount] = useState();
  const [loader, setLoader] = useState(false);
  const [selectedTab, setSelectedTab] = useState();
  const [balanceAmount, setBalanceAmount] = useState(0);
  const [selectedModeOfShowBalance, setSelectedModeOfShowBalance] =
    useState("always");
  const [
    isOpenSelectCurrencyForSwapModal,
    setIsOpenSelectCurrencyForSwapModal,
  ] = useState(false);
  const [isOpenSwapModal, setIsOpenSwapModal] = useState(false);
  const [selectedAssetForSwap, setSelectedAssetForSwap] = useState(null);
  const [allBalances, setAllBalances] = useState([]);
  const [selectedAssetForSwapBalance, setSelectedAssetForSwapBalance] =
    useState(0);

  useEffect(() => {
    setSelectedTab(taprootAsset?.uuid);
  }, [balanceAmount]);

  useEffect(() => {
    setSelectedModeOfShowBalance(currentSession?.onTap ? "onTap" : "always");
  }, [currentSession, selectedTab]);

  const updateSessionBalances = async (res) => {
    const balances = res?.available ?? [];
    const satsBalance =
      balances.find((data) => data?.target_currency === sats)?.amount ?? 0;
    const usdtRaw =
      balances.find((data) => data?.target_currency === usdt)?.amount ?? 0;

    const usdtBalance = showAmount({
      amount: usdtRaw,
      currency: usdt,
    });

    await sessionService.saveSession({
      ...session,
      satsBalance,
      usdtBalance,
    });
  };
  const fetchBalance = () => {
    setButtonDisabled(true);
    setLoader(true);
    callAPIInterface("GET", `/balances`)
      .then((res) => {
        setAllBalances(res?.available);
        const targetedBalance = res?.available?.find(
          (bal) => bal.target_currency === currency
        );
        updateSessionBalances(res);
        setBalanceAmount(targetedBalance?.amount);
        fetchExchangeRate(targetedBalance?.amount);
      })
      .catch((_err) => {
        setLoader(false);
        setButtonDisabled(false);
      });
  };

  const fetchExchangeRate = (amount) => {
    getExchangeRate(targetedCurrency, currency)
      .then((res) => {
        setLoader(false);
        setButtonDisabled(false);
        if (
          (res.target_highest_rate === null || res.target_highest_rate === 0) &&
          currency === sats
        ) {
          setIsSupportedCurrency(false);
          dispatch(
            showToast({
              isToastOpen: true,
              toastMessage: invalidExchangeRate(res?.currency),
              toastVariant: "warning",
              toastAutoHideDuration: 7000,
            })
          );
          setTargetAmount(0);
        } else {
          setIsSupportedCurrency(true);
          const amountToConvertFiat = stableCoins.includes(currency)
            ? amount / 100
            : amount;

          let targetedAmount = amountToConvertFiat / res?.target_lowest_rate;

          if (currency === "XAUT") {
            targetedAmount = (targetedAmount / 1000000).toFixed(6);
          }

          setTargetAmount(Number(targetedAmount) || 0);
        }
      })
      .catch((_e) => {
        setLoader(false);
        setButtonDisabled(false);
      });
  };

  useEffect(() => {
    fetchBalance();
  }, [currency]);

  const displayBalanceAmount = () => {
    if (balanceAmount) {
      const amount = getCurrencyAmount(balanceAmount, currency);
      const isNegativeAmount = Number(amount) < 0;
      const formattedAmt =
        showBtcAmount && currency === sats
          ? Math.abs(amount)
          : getAmountFormatted(Math.abs(amount), currency);
      const displayCurrency =
        showBtcAmount && currency === sats ? btc : currency;
      return (
        <>
          {displayAmountWithCurrency(
            displayCurrency,
            formattedAmt,
            isNegativeAmount
          )}
        </>
      );
    } else {
      return 0;
    }
  };

  const handleTabSelect = (value) => {
    if (!loader) {
      setSelectedTab(value);
      dispatch(setTransactionFilterData({}));
      const selectedCurrency = accountAssets?.find(
        (asset) => asset?.uuid === value
      );
      setSelectedAsset(selectedCurrency);
      dispatch(setTaprootCurrency(selectedCurrency));
    }
  };

  const handleOnClickAmount = async () => {
    currentSession?.onTap &&
      setSelectedModeOfShowBalance(
        selectedModeOfShowBalance === "always" ? "onTap" : "always"
      );
  };

  const handleOpenSwapModal = () => {
    setIsOpenSelectCurrencyForSwapModal(true);
  };

  const balanceAmountBoxProps = {
    className: `amount-section ${currencyColor}`,
    sx: {
      padding: "15px 15px",
      margin: "6px 0 10px",
      position: "relative",
    },
  };

  const swapModalProps = {
    isFromBrowser,
    isOpenSwapModal,
    setIsOpenSwapModal,
    selectedAssetForSwap,
    currency,
    currencyIcon,
    showBtcAmount,
    currentAssetBalance: balanceAmount,
    selectedAssetForSwapBalance,
    textSize: isFromBrowser ? 16 : 14,
    supportedSwaps,
  };

  return (
    <>
      <SelectionTabs
        data={accountAssets}
        selectedTab={selectedTab}
        handleAction={handleTabSelect}
        textSize={14}
        isFromBrowser={isFromBrowser}
        lastSelected={taprootAsset?.uuid}
        currencyColor={currencyColor}
        setSelectedAsset={setSelectedAsset}
      />
      {loader ? (
        <Box {...balanceAmountBoxProps}>
          <CommonSkeleton variant="circle" height={imgSize} width={imgSize} />
          <CommonSkeleton width={44} />
          <Box display="flex" flexDirection="column" alignItems="end">
            <Box>
              <Text
                className="default-text amount"
                size={satsAmountFontSize}
                font="semibold"
              >
                <CommonSkeleton height={44} />
              </Text>
            </Box>

            <Text
              className="grey-text"
              size={24}
              font="regular"
              variant="caption"
            >
              <CommonSkeleton width={100} />
            </Text>
          </Box>
          {walletBGImage}
        </Box>
      ) : Math.floor(balanceAmount) !== 0 ? (
        <Box {...balanceAmountBoxProps}>
          <Box display="flex" justifyContent="space-between">
            <Box sx={{ placeItems: "center" }}>
              <img
                src={currencyIcon}
                height={imgSize}
                width={imgSize}
                alt="currency icon"
              />
              <Text
                className={`txt-uppercase`}
                withIcon="end"
                size={16}
                font="regular"
              >
                {displayTaprootCurrency(balanceAmount, currency)}
              </Text>
            </Box>
            <WalletSymbols
              symbol="tune"
              action={handleSettings}
              customClass="cursor-pointer"
              sx={{ zIndex: "1" }}
            />
          </Box>
          <Box textAlign="right" sx={{ zIndex: "1" }}>
            <Box
              onClick={() => handleOnClickAmount()}
              sx={{ cursor: "pointer" }}
            >
              <Text
                className="grey-text"
                size={isFromBrowser ? 16 : 12}
                font="regular"
                variant="caption"
              >
                {currentBalance}
              </Text>
              <Text
                className="default-text amount"
                size={satsAmountFontSize}
                font="semibold"
              >
                {selectedModeOfShowBalance === "always"
                  ? displayBalanceAmount()
                  : "****"}
              </Text>
            </Box>
            <Text
              className="grey-text"
              size={24}
              font="regular"
              variant="caption"
            >
              {selectedModeOfShowBalance === "always" ? (
                <>
                  {"~"}
                  {targetedCurrencySymbol}
                  {targetAmount &&
                    getDisplayAmount(true, targetAmount, targetedCurrency)}
                </>
              ) : (
                "****"
              )}
              {isShowSwapButton && (
                <Box marginTop={"10px"}>
                  <Button
                    type="submit"
                    fullWidth
                    label={swap}
                    sx={{
                      backgroundColor: "white",
                    }}
                    icon="swapIcon"
                    iconPosition="start"
                    className={`button-icon-${currency}`}
                    onClick={handleOpenSwapModal}
                  />
                </Box>
              )}
            </Text>
          </Box>
          {walletBGImage}
        </Box>
      ) : (
        <NewToSpeedWallet
          isFromBrowser={isFromBrowser}
          currency={currency}
          currencyIcon={currencyIcon}
          currencyColor={currencyColor}
          walletBGImage={walletBGImage}
          balances={allBalances}
          balanceAmount={balanceAmount}
          isShowSwapButton={isShowSwapButton}
          supportedSwaps={supportedSwaps}
        />
      )}

      {isOpenSelectCurrencyForSwapModal && (
        <SwapCurrencyDrawer
          isOpenModal={isOpenSelectCurrencyForSwapModal}
          setIsOpenModal={setIsOpenSelectCurrencyForSwapModal}
          isFromBrowser={isFromBrowser}
          balances={allBalances}
          setSelectedAssetForSwap={setSelectedAssetForSwap}
          setIsOpenSwapModal={setIsOpenSwapModal}
          setSelectedAssetForSwapBalance={setSelectedAssetForSwapBalance}
          supportedSwaps={supportedSwaps}
        />
      )}

      {isOpenSwapModal && <SwapCommon {...swapModalProps} />}
    </>
  );
}

export default React.memo(WalletAmountBox);
